import {
  Component,
  DestroyRef,
  EventEmitter,
  inject,
  Input,
  OnChanges,
  OnDestroy,
  Output,
  SimpleChanges,
  signal
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { CommonModule } from '@angular/common';
import {
  FormBuilder,
  ReactiveFormsModule,
  Validators
} from '@angular/forms';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { HttpErrorResponse } from '@angular/common/http';
import { Subscription } from 'rxjs';

import { WellsService } from 'src/app/services/wells.service';
import { Well } from '@models/well.interface';
import { SaveWellDto } from '@models/save-well-dto.interface';
import { RigLookupDto } from '@models/project-lookups-dto.interface';
import { LookupDto } from '@models/lookup-dto.interface';

const NON_GOA_WELL_NAME_PLACEHOLDER = 'N/A';

/** Emitted when a save completes so the parent can refresh and re-select. */
export interface WellSavedEvent {
  id: number;
}

@Component({
  selector: 'app-well-detail',
  standalone: true,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    MatProgressSpinnerModule
  ],
  templateUrl: './well-detail.component.html',
  styleUrl: './well-detail.component.scss'
})
export class WellDetailComponent implements OnChanges, OnDestroy {
  @Input() well: Well | null = null;
  @Input() rigs: RigLookupDto[] = [];
  @Input() businessUnits: LookupDto[] = [];

  @Output() saved = new EventEmitter<WellSavedEvent>();

  private readonly wellsService = inject(WellsService);
  private readonly fb = inject(FormBuilder);
  private readonly destroyRef = inject(DestroyRef);
  private saveRequest?: Subscription;

  protected isSaving = signal(false);
  protected errorMessage = signal('');
  protected isDeepwaterFormatSelected = signal(false);

  protected form = this.fb.group({
    defaultRigId:   [null as number | null],
    name:           [null as string | null, [Validators.required, Validators.maxLength(200)]],
    prospectName:   [null as string | null, Validators.maxLength(100)],
    prospectNumber: [null as string | null, Validators.maxLength(20)],
    field:          [null as string | null, Validators.maxLength(100)],
    blockNumber:    [null as string | null, Validators.maxLength(20)]
  });

  constructor() {
    this.form.get('defaultRigId')!.valueChanges.pipe(
      takeUntilDestroyed(this.destroyRef)
    ).subscribe((rigId) => this.onRigChanged(rigId, false));
  }

  ngOnChanges(changes: SimpleChanges): void {
    const wellChanged = !!changes['well'];

    if (wellChanged && this.well) {
      const isDeepwaterFormat = this.isDeepwaterFormatRig(this.well.defaultRigId);

      this.form.reset();
      this.form.patchValue({
        defaultRigId:   this.well.defaultRigId,
        name:           this.well.name ?? '',
        prospectName:   isDeepwaterFormat ? this.well.prospectName : (this.well.prospectName ?? this.well.name ?? ''),
        prospectNumber: this.well.prospectNumber,
        field:          this.well.field,
        blockNumber:    this.well.blockNumber
      }, { emitEvent: false });
    } else if (wellChanged) {
      this.form.reset();
      this.isDeepwaterFormatSelected.set(false);
    }

    if (this.well && (changes['well'] || changes['rigs'] || changes['businessUnits'])) {
      this.onRigChanged(this.form.get('defaultRigId')!.value, true);
    }

    this.errorMessage.set('');
    this.form.enable();
  }

  ngOnDestroy(): void {
    this.saveRequest?.unsubscribe();
  }

  get isNew(): boolean {
    return !this.well || this.well.id === 0;
  }

  protected focusNextInput(event: KeyboardEvent): void {
    const form = event.currentTarget as HTMLFormElement;
    const target = event.target;
    if (!(target instanceof HTMLElement)) return;

    const editableControls = Array.from(form.querySelectorAll<HTMLElement>(
      'input[formControlName]:not([type="hidden"]):not([type="button"]):not([type="submit"]):not([type="reset"]):not([readonly]):not([disabled]):not([tabindex="-1"]), select[formControlName]:not([disabled]):not([tabindex="-1"])'
    ));
    const currentIndex = editableControls.indexOf(target);
    if (currentIndex < 0) return;

    event.preventDefault();
    if (currentIndex < editableControls.length - 1) {
      editableControls[currentIndex + 1].focus();
    }
  }

  protected rigDisplayName(r: RigLookupDto): string {
    return r.alias ? `${r.name} (${r.alias})` : (r.name ?? '');
  }

  private onRigChanged(rigId: number | null, preserveValuesOnLoad: boolean): void {
    const nameControl = this.form.get('name')!;
    const prospectNameControl = this.form.get('prospectName')!;

    const isDeepwaterFormat = this.isDeepwaterFormatRig(rigId);
    this.isDeepwaterFormatSelected.set(isDeepwaterFormat);

    if (!isDeepwaterFormat) {
      if (!preserveValuesOnLoad) {
        nameControl.setValue(NON_GOA_WELL_NAME_PLACEHOLDER, { emitEvent: false });
        this.form.get('prospectNumber').setValue('');
        this.form.get('field').setValue('');
        this.form.get('blockNumber').setValue('');
      }

      nameControl.disable({ emitEvent: false });
      prospectNameControl.setValidators([Validators.required, Validators.maxLength(100)]);
    } else {
      nameControl.enable({ emitEvent: false });
   
      if (nameControl.value === NON_GOA_WELL_NAME_PLACEHOLDER) {
        nameControl.setValue(null, { emitEvent: false });
      }
      prospectNameControl.setValidators(Validators.maxLength(100));
    }
    prospectNameControl.updateValueAndValidity({ emitEvent: false });
  }

  private isDeepwaterFormatRig(rigId: number | null): boolean {
    const rig = rigId ? this.rigs.find(r => r.id === rigId) : null;
    const businessUnit = rig?.defaultBusinessUnitId
      ? this.businessUnits.find(bu => bu.id === rig.defaultBusinessUnitId)
      : null;
    return businessUnit?.applyDeepwaterFormat === true;
  }

  protected onSave(): void {
    if (this.form.invalid || this.isSaving()) return;

    const raw = this.form.getRawValue();
    const dto: SaveWellDto = {
      name:           raw.name!.trim(),
      prospectName:   raw.prospectName?.trim() ?? null,
      prospectNumber: raw.prospectNumber?.trim() ?? null,
      field:          raw.field?.trim() ?? null,
      blockNumber:    raw.blockNumber?.trim() ?? null,
      defaultRigId:   raw.defaultRigId
    };

    this.isSaving.set(true);
    this.errorMessage.set('');
    this.clearServerErrors();
    const request$ = this.isNew
      ? this.wellsService.create(dto)
      : this.wellsService.update(this.well!.id, dto);

    this.saveRequest = request$.subscribe({
      next: savedWell => {
        this.isSaving.set(false);
        this.saved.emit({ id: savedWell.id });
      },
      error: (err: HttpErrorResponse) => {
        this.isSaving.set(false);
        this.handleServerError(err);
      }
    });
  }

  private handleServerError(err: HttpErrorResponse): void {
    const body = err.error;

    // Map field-level errors from ValidationProblem response onto form controls.
    if (body?.errors && typeof body.errors === 'object') {
      const errors = body.errors as Record<string, string[]>;

      let mapped = false;
      for (const [serverField, messages] of Object.entries(errors)) {
        const control = this.form.get(serverField.toLowerCase());
        if (control) {
          control.setErrors({ serverError: messages[0] });
          control.markAsTouched();
          mapped = true;
        }
      }

      if (!mapped) {
        this.errorMessage.set('Save failed. Please check your entries and try again.');
      }
    } else {
      this.errorMessage.set('An unexpected error occurred. Please try again.');
    }
  }

  private clearServerErrors(): void {
    for (const key of Object.keys(this.form.controls)) {
      const control = this.form.get(key);
      if (control?.hasError('serverError')) {
        const { serverError: _, ...rest } = control.errors ?? {};
        control.setErrors(Object.keys(rest).length > 0 ? rest : null);
      }
    }
  }
}
